/**
 * The Captain's Primer — the game's illustrated MANUAL, NOT a questline (owner mandate, 2026-07-16: "ninguém
 * quer entrar num jogo e sair fazendo missão de meia hora. Você só diz pra ele os comandos, como é que joga,
 * aonde vê isso, aonde vê aquilo"). Prod telemetry proved the quest-chain FTUE collapsed (76 spawns → 2
 * loot_done → 1 sell_done), so the tutorial chain is GONE. Onboarding is now two layers:
 *   · the Helm Coach (coach.ts) — contextual coach-marks on the LIVE HUD, the brand-new captain's path;
 *   · THIS sheet — a dismissible parchment manual of swipeable illustrated cards, opened on demand from the
 *     Book's "How to Play" chapter, that only SHOWS how the game is played and where things live (helm &
 *     sails, broadsides & finite rounds, bailing, the chart, plunder & selling, ports, the home base).
 * No gates, no objectives, no tracked progress.
 *
 * Behaviour:
 *  · Opened from the menu only (main.ts wires `captainsBook.onOpenManual`); never pops up on its own.
 *  · Dismissed with one tap ("Set sail" / ✕ / backdrop / Esc).
 *  · Visual language: the game's aged-sea-chart parchment kit (parchment.ts `.pg-*`) — wood frame, gilt rope,
 *    brass studs, Cinzel cartouche, wax-seal pagination. NO generic dark-glass.
 *  · Illustrations are hand-painted plates (SDXL, art/source/primer-panels) served as small lazy webp files
 *    from /primer/ — they load only when the sheet opens, so the boot budget is untouched.
 *  · Telemetry (no PII): `primer_seen` on this device's first open, `primer_reopened` on later opens,
 *    `primer_dismissed` carrying the deepest card reached (`k = p1..pN`) + seconds open (`d`).
 *
 * The decision logic + telemetry payload math are PURE (unit-tested with no DOM); the class is a thin DOM
 * controller in the style of the other body-level overlays. Client-only, ZERO wire.
 */
import { injectParchmentCss } from './parchment.js';

const LS_SEEN = 'sor:primerSeen';

/** One swipeable card: a painted plate + a couple of "here's how / here's where" lines. All player-facing
 *  text is ENGLISH (the game is 100% EN on screen). */
export interface PrimerPanel {
  key: string;
  icon: string;
  img: string;    // /primer/<key>.webp — lazy-loaded when the sheet opens (never on the boot path)
  title: string;
  body: string;   // short — commands + where to look, never an errand
}

export const PRIMER_PANELS: readonly PrimerPanel[] = [
  {
    key: 'helm', icon: '⛵', img: '/primer/helm.webp', title: 'Take the Helm',
    body: 'Drag the wheel to steer and tap ⛵ to set your sails — the wind does the rest. Tap ⚓ to drop or weigh anchor.',
  },
  {
    key: 'broadside', icon: '💥', img: '/primer/broadside.webp', title: 'Fire a Broadside',
    body: 'Tap PORT or STARBOARD to loose your cannons — lead a moving target. Rounds run out: buy more at any port or craft your own.',
  },
  {
    key: 'bail', icon: '🪣', img: '/primer/bail.webp', title: 'Stay Afloat',
    body: 'Hits spring leaks and the sea pours in. HOLD 🪣 to bail her dry before she goes down — your hull is your health.',
  },
  {
    key: 'chart', icon: '🗺', img: '/primer/chart.webp', title: 'Read the Chart',
    body: 'Tap the sea chart to set a course — your quartermaster sails there for you. Long-press to drop an Armada marker. The minimap keeps watch up top.',
  },
  {
    key: 'treasure', icon: '💰', img: '/primer/treasure.webp', title: 'Plunder & Sell',
    body: 'Golden beacons mark floating treasure — glide over a chest to hoist it, then sell it inside any outpost ring. Gold, cargo & hold ride the top bar.',
  },
  {
    key: 'port', icon: '🏛', img: '/primer/port.webp', title: 'Step Ashore',
    body: 'Anchor inside a port ring to walk ashore: trade at the market, hit the tavern casino, and stock ammo & gear at the Ship\'s Stores.',
  },
  {
    key: 'base', icon: '🏝', img: '/primer/base.webp', title: 'Your Home Base',
    body: 'Your island is yours: bank loot at the dock, upgrade the harbor works, decorate your house — and raid your rivals\' islands for theirs.',
  },
] as const;

/** PURE: which telemetry event does this open fire? The device's FIRST look at the manual is `primer_seen`
 *  (the reach signal); every later open is `primer_reopened` (the it-was-useful-enough-to-return signal). */
export function primerOpenEvent(seenBefore: boolean): 'seen' | 'reopened' {
  return seenBefore ? 'reopened' : 'seen';
}

/** PURE: the `primer_dismissed` telemetry payload — deepest card reached (1-based, `p1..pN`) + seconds open.
 *  Clamped so a junk clock or index can never emit a malformed key. */
export function primerDismissMeta(maxPanelIndex: number, openedAtMs: number, nowMs: number): { k: string; d: number } {
  const i = Math.max(0, Math.min(PRIMER_PANELS.length - 1, Math.trunc(Number.isFinite(maxPanelIndex) ? maxPanelIndex : 0)));
  const ms = Math.max(0, nowMs - openedAtMs);
  return { k: `p${i + 1}`, d: Math.round(ms / 1000) };
}

export interface PrimerDeps {
  /** UI tick on open/dismiss/page. */
  sfx?: { ui: () => void };
  /** Telemetry hooks (fire-and-forget, no PII). */
  tele?: {
    seen?: () => void;                       // first-ever auto-show
    reopened?: () => void;                   // re-opened from the menu
    dismissed?: (k: string, d: number) => void; // deepest panel + seconds open
  };
  /** Injectable clock for tests. */
  clock?: () => number;
}

export class Primer {
  private readonly clock: () => number;
  private root: HTMLElement | null = null;
  private strip: HTMLElement | null = null;
  private dots: HTMLElement[] = [];
  private openedAt = 0;
  private panel = 0;
  private maxPanel = 0;
  private openReason: 'first' | 'menu' | null = null;
  private onKey = (e: KeyboardEvent): void => {
    if (!this.isOpen()) return;
    if (e.key === 'Escape') this.dismiss();
    else if (e.key === 'ArrowRight') this.goTo(this.panel + 1);
    else if (e.key === 'ArrowLeft') this.goTo(this.panel - 1);
  };

  constructor(private readonly host: HTMLElement, private readonly deps: PrimerDeps = {}) {
    this.clock = deps.clock ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  }

  /** Open the manual (from the Book's How to Play chapter). The device's first-ever open fires `primer_seen`;
   *  later opens fire `primer_reopened` — see primerOpenEvent. */
  open(): void {
    if (this.root) return;
    let seenBefore = false;
    try { seenBefore = localStorage.getItem(LS_SEEN) === '1'; } catch { /* private mode */ }
    this.openReason = seenBefore ? 'menu' : 'first';
    this.panel = 0; this.maxPanel = 0;
    this.openedAt = this.clock();
    try { localStorage.setItem(LS_SEEN, '1'); } catch { /* private mode */ }
    this.mount();
    this.deps.sfx?.ui();
    if (primerOpenEvent(seenBefore) === 'seen') this.deps.tele?.seen?.();
    else this.deps.tele?.reopened?.();
  }

  /** One tap and the captain is free. Persists (already stamped on open) + reports how far they read. */
  dismiss(): void {
    if (!this.root) return;
    const meta = primerDismissMeta(this.maxPanel, this.openedAt, this.clock());
    this.deps.tele?.dismissed?.(meta.k, meta.d);
    this.deps.sfx?.ui();
    const el = this.root;
    this.root = null; this.strip = null; this.dots = []; this.openReason = null;
    try { document.removeEventListener('keydown', this.onKey); } catch { /* non-DOM test host */ }
    el.classList.remove('on');
    setTimeout(() => el.remove(), 320);
  }

  isOpen(): boolean { return this.root !== null; }

  /** Read-only introspection for tests + the headless smoke hook. */
  debug(): { open: boolean; panel: number; maxPanel: number; reason: 'first' | 'menu' | null } {
    return { open: this.isOpen(), panel: this.panel, maxPanel: this.maxPanel, reason: this.openReason };
  }

  /* --------------------------------------- view --------------------------------------- */

  private goTo(i: number): void {
    const n = Math.max(0, Math.min(PRIMER_PANELS.length - 1, i));
    if (!this.strip) return;
    this.strip.scrollTo({ left: n * this.strip.clientWidth, behavior: 'smooth' });
    this.setPanel(n);
  }

  private setPanel(n: number): void {
    if (n === this.panel) return;
    this.panel = n;
    this.maxPanel = Math.max(this.maxPanel, n);
    this.deps.sfx?.ui();
    this.dots.forEach((d, i) => d.classList.toggle('on', i === n));
    const prev = this.root?.querySelector('.pr-prev') as HTMLButtonElement | null;
    const next = this.root?.querySelector('.pr-next') as HTMLButtonElement | null;
    if (prev) prev.disabled = n === 0;
    if (next) next.disabled = n === PRIMER_PANELS.length - 1;
  }

  private mount(): void {
    injectParchmentCss();
    if (!document.getElementById('pr-style')) {
      const st = document.createElement('style'); st.id = 'pr-style';
      st.textContent = PRIMER_CSS;
      document.head.appendChild(st);
    }
    const el = document.createElement('div');
    el.id = 'primer';
    const cards = PRIMER_PANELS.map((p, i) => `
      <section class="pr-card" data-i="${i}">
        <div class="pr-art"><img ${i === 0 ? '' : 'loading="lazy" '}src="${p.img}" alt="" draggable="false"></div>
        <div class="pr-tt"><span class="pr-ic">${p.icon}</span><b class="pg-title pr-t">${p.title}</b></div>
        <p class="pr-b">${p.body}</p>
      </section>`).join('');
    const dots = PRIMER_PANELS.map((_, i) => `<i class="pr-dot${i === 0 ? ' on' : ''}"></i>`).join('');
    el.innerHTML = `
      <div class="pr-sheet pg-frame" role="dialog" aria-label="Captain's Primer">
        <div class="pg-paper pr-paper">
          <div class="pr-head">
            <span class="pg-title pr-cart">Captain's Primer</span>
            <button class="pr-x" type="button" aria-label="Close">✕</button>
          </div>
          <hr class="pg-rule">
          <div class="pr-strip">${cards}</div>
          <div class="pr-nav">
            <button class="pr-arrow pr-prev" type="button" aria-label="Previous" disabled>‹</button>
            <span class="pr-dots">${dots}</span>
            <button class="pr-arrow pr-next" type="button" aria-label="Next">›</button>
          </div>
          <button class="pr-go" type="button">⚓&nbsp; Set Sail</button>
        </div>
      </div>`;
    this.host.appendChild(el);
    this.root = el;
    this.strip = el.querySelector('.pr-strip') as HTMLElement;
    this.dots = Array.from(el.querySelectorAll('.pr-dot')) as HTMLElement[];
    // wiring — every dismiss route is ONE tap: the big Set Sail, the ✕, the backdrop, or Esc.
    (el.querySelector('.pr-go') as HTMLElement).addEventListener('click', () => this.dismiss());
    (el.querySelector('.pr-x') as HTMLElement).addEventListener('click', () => this.dismiss());
    el.addEventListener('click', (e) => { if (e.target === el) this.dismiss(); });
    (el.querySelector('.pr-prev') as HTMLElement).addEventListener('click', () => this.goTo(this.panel - 1));
    (el.querySelector('.pr-next') as HTMLElement).addEventListener('click', () => this.goTo(this.panel + 1));
    this.strip.addEventListener('scroll', () => {
      if (!this.strip || this.strip.clientWidth <= 0) return;
      this.setPanel(Math.round(this.strip.scrollLeft / this.strip.clientWidth));
    }, { passive: true });
    document.addEventListener('keydown', this.onKey);
    requestAnimationFrame(() => el.classList.add('on'));
  }
}

/* The sheet cases pixel-for-pixel with the chart HUD: the shared .pg-frame/.pg-paper parchment kit carries the
 * wood band + gilt rope + brass studs + aged paper; below is only the primer-specific layout — the plate (an
 * inked "painted plate" like a chapter illustration in an old pilot book), the Cinzel cartouche, the wax-seal
 * pagination dots and the brass Set Sail bar. Mobile-first: ≥44px touch targets, one column, swipeable strip. */
const PRIMER_CSS = `
#primer{ position:fixed; inset:0; z-index:80; display:flex; align-items:center; justify-content:center;
  padding:calc(env(safe-area-inset-top,8px) + 10px) 12px calc(env(safe-area-inset-bottom,8px) + 10px);
  background:rgba(4,12,20,.62); opacity:0; transition:opacity .3s ease; }
#primer.on{ opacity:1; }
#primer .pr-sheet{ width:min(94vw,430px); max-height:min(92svh,720px); display:flex; transform:translateY(10px) scale(.985);
  transition:transform .3s ease; }
#primer.on .pr-sheet{ transform:translateY(0) scale(1); }
#primer .pr-paper{ flex:1; display:flex; flex-direction:column; min-height:0; padding:12px 12px 12px; }
#primer .pr-head{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:2px 2px 0; }
#primer .pr-cart{ font-size:16px; }
#primer .pr-x{ flex:none; width:44px; height:44px; margin:-8px -8px -6px 0; border:0; background:none; cursor:pointer;
  font-size:17px; font-weight:800; color:#6a4c22; border-radius:10px; }
#primer .pr-x:active{ background:rgba(90,58,18,.12); }
#primer .pr-strip{ flex:1; min-height:0; display:flex; overflow-x:auto; overflow-y:hidden;
  scroll-snap-type:x mandatory; scrollbar-width:none; -webkit-overflow-scrolling:touch; margin:4px -2px 0; }
#primer .pr-strip::-webkit-scrollbar{ display:none; }
#primer .pr-card{ flex:0 0 100%; min-width:100%; scroll-snap-align:center; scroll-snap-stop:always;
  display:flex; flex-direction:column; padding:4px 4px 0; }
/* the painted PLATE — an inked neat-line around the illustration, like a plate pasted into a pilot book */
#primer .pr-art{ position:relative; border-radius:6px; overflow:hidden; aspect-ratio:3/2; max-height:38svh;
  box-shadow:inset 0 0 0 1px rgba(58,40,16,.55), inset 0 0 0 3px rgba(58,40,16,.16), 0 2px 8px rgba(40,24,6,.28); }
#primer .pr-art img{ width:100%; height:100%; object-fit:cover; display:block; filter:saturate(.96) sepia(.06); }
#primer .pr-art::after{ content:''; position:absolute; inset:0; pointer-events:none;
  box-shadow:inset 0 0 34px rgba(70,44,18,.30), inset 0 0 90px rgba(34,20,6,.16); }
#primer .pr-tt{ display:flex; align-items:center; gap:8px; margin:10px 2px 0; }
#primer .pr-ic{ font-size:20px; line-height:1; filter:drop-shadow(0 1px 1px rgba(60,38,10,.4)); }
#primer .pr-t{ font-size:14.5px; }
#primer .pr-b{ margin:6px 2px 8px; font-size:13px; line-height:1.5; font-weight:600; color:#3b2712;
  font-family:'Iowan Old Style','Palatino Linotype','Book Antiqua',Palatino,Georgia,serif; }
#primer .pr-nav{ flex:none; display:flex; align-items:center; justify-content:space-between; gap:8px; margin:0 2px 6px; }
#primer .pr-arrow{ width:44px; height:44px; border-radius:10px; cursor:pointer; font-size:22px; line-height:1;
  color:#42280a; background:linear-gradient(180deg, rgba(222,186,102,.7), rgba(150,110,40,.5));
  border:1px solid rgba(90,60,16,.6); box-shadow:inset 0 1px 0 rgba(255,246,214,.55), 0 1px 2px rgba(40,24,6,.28); }
#primer .pr-arrow:disabled{ opacity:.32; cursor:default; }
/* pagination = a row of little wax seals: dull sealing-wax when passed, bright crimson when current */
#primer .pr-dots{ display:flex; gap:9px; align-items:center; }
#primer .pr-dot{ width:11px; height:11px; border-radius:50%;
  background:radial-gradient(circle at 36% 32%, #caa27a, #8a6a4a 65%, #5c4630); opacity:.55;
  box-shadow:inset 0 -1px 1px rgba(40,24,6,.4), 0 1px 1px rgba(255,246,214,.35); transition:all .25s ease; }
#primer .pr-dot.on{ opacity:1; transform:scale(1.25);
  background:radial-gradient(circle at 36% 32%, #d96a52, #a3282e 60%, #5e0f14);
  box-shadow:inset 0 -1px 2px rgba(40,4,4,.5), 0 0 7px rgba(163,40,46,.55); }
/* the one big brass "you're free" bar — the only call to action on the sheet */
#primer .pr-go{ flex:none; min-height:50px; margin:0 2px 2px; border-radius:11px; cursor:pointer;
  font-family:'Cinzel',Georgia,serif; font-weight:900; font-size:15px; letter-spacing:.12em; text-transform:uppercase;
  color:#2a1c06; background:linear-gradient(180deg,#e9c869,#c99a3a 55%,#a97b22);
  border:1px solid #7a5416; box-shadow:inset 0 1px 0 rgba(255,246,214,.6), inset 0 -2px 4px rgba(60,38,10,.35), 0 3px 10px rgba(40,24,6,.35); }
#primer .pr-go:active{ transform:translateY(1px); }
@media (min-width:760px){
  #primer .pr-sheet{ width:min(92vw,470px); }
  #primer .pr-cart{ font-size:18px; }
  #primer .pr-t{ font-size:16px; }
  #primer .pr-b{ font-size:13.5px; }
}
`;
